import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Activity,
  AlertCircle,
  Archive,
  ArchiveRestore,
  Bot,
  FileText,
  Hash,
  KeyRound,
  Link2,
  Loader,
  Package,
  Pencil,
  Rocket,
  Settings2,
  Sparkles,
  ShieldCheck,
  ShieldOff,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { EmptyState } from '../ui/EmptyState';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import type { PartnerActivity } from '../../types/partners';
import { partnerPagePath } from '../../utils/partnerLinks';

const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  member_added: UserPlus,
  member_removed: UserMinus,
  member_tier: Users,
  member_forgotten: UserMinus,
  channel_created: Hash,
  channel_linked: Link2,
  channel_unlinked: Link2,
  channel_archived: Archive,
  channel_restored: ArchiveRestore,
  channel_missing: AlertCircle,
  channel_misplaced: AlertCircle,
  access_granted: KeyRound,
  access_revoked: ShieldOff,
  role_granted: ShieldCheck,
  role_removed: ShieldOff,
  roles_held: ShieldCheck,
  report_received: FileText,
  report_status: FileText,
  report_updated: FileText,
  report_cf_listed: Sparkles,
  report_cf_detected: Sparkles,
  report_live: Rocket,
  report_superseded: FileText,
  pack_linked: Package,
  pack_unlinked: Package,
  discord_settings: Settings2,
  member_note: Pencil,
  access_revoke_requested: ShieldOff,
};

const iconFor = (action: string) => ICONS[action] ?? (action.startsWith('report') ? FileText : Activity);

const PAGE = 50;

interface PartnerActivityTimelineProps {
  creatorId: string;
  /** Changes when the page reloads, so a write shows up here without a manual refresh. */
  refreshKey?: number;
  /** Discord ID → name, so an entry about someone says who. */
  names?: ReadonlyMap<string, string>;
}

/** The partner's timeline: team changes, channel events and report progress, newest first. */
export function PartnerActivityTimeline({ creatorId, refreshKey = 0, names }: PartnerActivityTimelineProps) {
  const [items, setItems] = useState<PartnerActivity[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await partnersApi.activity(creatorId, { limit: PAGE });
      setItems(page.items);
      setNextBefore(page.next_before);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The activity could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [creatorId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const more = async () => {
    if (nextBefore === null) return;
    setLoadingMore(true);
    try {
      const page = await partnersApi.activity(creatorId, { before: nextBefore, limit: PAGE });
      setItems((prev) => [...prev, ...page.items.filter((item) => !prev.some((p) => p.id === item.id))]);
      setNextBefore(page.next_before);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'More activity could not be loaded.');
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading && items.length === 0) {
    return (
      <div className="space-y-4" aria-busy="true">
        <SkeletonLoader className="h-4 w-28" />
        <div className="space-y-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <SkeletonLoader className="w-6 h-6 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-3.5 w-3/4" />
                <SkeletonLoader className="h-3 w-40" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="The activity could not be loaded"
        description={error}
        action={
          <button type="button" className="glass-button" onClick={() => void load()}>
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader title="Activity" icon={Activity} count={items.length} />
      {items.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={Activity}
          title="Nothing has happened yet"
          description="Team changes, what Mr. Roboto does in Discord and every update report show up here."
        />
      ) : (
        <div className="relative">
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-glass-200/20" />
          <div>
            {items.map((entry) => {
              const Icon = iconFor(entry.action);
              const byBot = entry.actor_label === 'Mr. Roboto';
              return (
                <div key={entry.id} className="relative flex gap-4 pb-5 last:pb-0">
                  <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-accent-500/10 border border-accent-500/30 flex items-center justify-center">
                    <Icon className="w-3 h-3 text-accent-300" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-sm text-glass-200 break-words">
                      {entry.subject_discord_id && (
                        <span className="font-medium text-white" title={`Discord ID ${entry.subject_discord_id}`}>
                          {names?.get(entry.subject_discord_id) ?? entry.subject_discord_id}:{' '}
                        </span>
                      )}
                      {entry.description}
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-glass-500">
                      {entry.actor_label && (
                        <span className="inline-flex items-center gap-1 font-medium text-glass-300">
                          {byBot && <Bot className="w-3 h-3 text-accent-300" />}
                          {entry.actor_label}
                        </span>
                      )}
                      <span className="tabular-nums">{formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}</span>
                      {entry.report_id && (
                        <Link to={partnerPagePath(creatorId, 'updates', entry.report_id)} className="link">
                          Open the report
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {error && items.length > 0 && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      {nextBefore !== null && (
        <button type="button" className="glass-button-secondary" onClick={() => void more()} disabled={loadingMore}>
          {loadingMore ? <Loader className="w-4 h-4 animate-spin" /> : null}
          Show older activity
        </button>
      )}
    </div>
  );
}
